import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tray } from '@phosphor-icons/react';
import { LotesKanbanBoard } from '@/components/lotes-kanban/LotesKanbanBoard';
import { LoteDetailModal } from '@/components/lotes-kanban/LoteDetailModal';
import { SegmentedControl } from '@/components/common/SegmentedControl';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { PROYECTOS, type ProyectoCode } from '@/types/sitePlan';
import { useAppStore } from '@/store/appStore';
import { useLotesKanban } from '@/hooks/useLotesKanban';

// Vista del kanban de tramites. Permite elegir proyecto + filtrar por
// el "Creador APC" (colaborador que subio el lote a instituciones).
// La seleccion de proyecto se persiste en localStorage para que volver
// a la tab no resetee la eleccion. Independiente de la del SitePlan
// para no acoplar las dos vistas.
const STORAGE_KEY = 'arqui-flow-tramites-project';

function readStoredProyecto(): ProyectoCode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'VN' || raw === 'VI' || raw === 'VB') return raw;
  } catch { /* localStorage no disponible */ }
  return 'VN';
}

export function LotesKanbanPage() {
  const [proyecto, setProyecto] = useState<ProyectoCode>(readStoredProyecto);
  const search = useAppStore(s => s.search);
  const creadorApcId = useAppStore(s => s.tramitesCreadorApcId);
  const setCreadorApcId = useAppStore(s => s.setTramitesCreadorApcId);

  // Cargamos los lotes del proyecto solo para derivar la lista de
  // creadores que aparecen en el (no queremos un combo con TODOS los
  // colaboradores; solo los relevantes). Reusa el cache del Kanban
  // gracias a la misma queryKey.
  const { data: lotes = [] } = useLotesKanban(proyecto);

  const creadoresOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const l of lotes) {
      if (l.idCreadorApc != null && !seen.has(l.idCreadorApc)) {
        seen.set(l.idCreadorApc, l.nomCreadorApc || `IDCol ${l.idCreadorApc}`);
      }
    }
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [lotes]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, proyecto); } catch { /* idem */ }
  }, [proyecto]);

  // Si el creador filtrado deja de aparecer en el proyecto actual
  // (ej. cambiaste de VN a VI y ese creador no toca lotes de VI), limpio.
  useEffect(() => {
    if (creadorApcId !== null && !creadoresOptions.some(o => o.value === creadorApcId)) {
      setCreadorApcId(null);
    }
  }, [creadorApcId, creadoresOptions, setCreadorApcId]);

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-3 px-3 py-2 border-b border-brand-gray-200 bg-white flex-wrap">
        <span className="text-xs font-medium text-stone-600">Proyecto</span>
        <SegmentedControl<ProyectoCode>
          options={[
            { value: 'VN', label: PROYECTOS.VN.nombre },
            { value: 'VI', label: PROYECTOS.VI.nombre },
            { value: 'VB', label: PROYECTOS.VB.nombre },
          ]}
          value={proyecto}
          onChange={setProyecto}
        />

        <div className="h-5 w-px bg-stone-200" aria-hidden />

        <span className="text-xs font-medium text-stone-600">Creador APC</span>
        <div className="w-56">
          <SearchableSelect<number>
            options={creadoresOptions}
            value={creadorApcId}
            onChange={setCreadorApcId}
            placeholder="— Todos —"
            emptyText={lotes.length === 0 ? 'Sin lotes' : 'Sin creadores'}
          />
        </div>

        {/* Entrada a la bandeja de bootstrap CFIA (B3). Vive aca, no en el
            nav top-level: es un andamio transitorio (9.0), no una vista fija. */}
        <Link
          to="/tramites/bootstrap"
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-brand-gray-300 bg-white px-3 py-1.5 text-sm text-brand-gray-500 hover:bg-brand-gray-100"
        >
          <Tray size={16} weight="bold" /> Bandeja de bootstrap
        </Link>
      </div>

      <div className="flex-1 min-h-0">
        <LotesKanbanBoard
          proyecto={proyecto}
          search={search}
          creadorApcId={creadorApcId}
        />
      </div>

      <LoteDetailModal proyecto={proyecto} />
    </div>
  );
}
